import {
  ConnectionState,
  WebSocketMessage,
  PendingRequest,
  RequestOptions,
} from '../../common/types';
import { MessageQueue } from './MessageQueue';
import { ConnectionStatus } from './ConnectionStatus';
import logger from '@ayocore/platform/common/helpers/logger';

// Typed global singleton slot on window
type AyaWsInstance = {
  disconnect: () => void;
  cleanup?: () => void;
};

declare global {
  interface Window {
    __AYA_WEBSOCKET_INSTANCE__?: AyaWsInstance;
  }
}

export class WebSocketConnection {
  /** Активное WebSocket-соединение */
  private ws: WebSocket | null = null;

  /** Подписчики на входящие сообщения */
  private messageHandlers: ((webSocketMessage: WebSocketMessage) => void)[] = [];
  /** Подписчики на успешное подключение */
  private connectHandlers: (() => void)[] = [];
  /** Подписчики на разрыв соединения */
  private disconnectHandlers: (() => void)[] = [];
  /** Подписчики на ошибки транспорта */
  private errorHandlers: ((error: Error) => void)[] = [];

  /** Очередь сообщений (localStorage) — переживает перезагрузку страницы */
  private messageQueue = new MessageQueue();
  /** Состояние подключения (CONNECTED / DISCONNECTED / CONNECTING) + счётчик reconnect */
  private connectionStatus = new ConnectionStatus();
  /** Ожидающие ответа request-response запросы (messageId → callback) */
  private pendingRequests = new Map<string, PendingRequest>();

  /** Интервал между попытками reconnect (мс) */
  private reconnectInterval: number = 3000;
  /** Максимальное количество попыток reconnect */
  private maxReconnectAttempts: number = 100;
  /** Таймаут на установку соединения (мс) */
  private connectionTimeout: number = 5000;
  /** Таймер отложенного reconnect */
  private reconnectTimer: NodeJS.Timeout | null = null;
  /** Таймер таймаута подключения */
  private connectionTimer: NodeJS.Timeout | null = null;
  /** Флаг явного отключения (disconnect() вызван вручную — не ретраить) */
  private isIntentionalDisconnect: boolean = false;

  constructor() {
    // Handle singleton cleanup in the low-level class
    this.cleanupExistingInstance();

    // Process any queued messages from previous session on startup
    this.processQueueOnStartup();
  }

  /**
   * Handle singleton pattern - cleanup existing instance and register this one
   */
  private cleanupExistingInstance(): void {
    if (window.__AYA_WEBSOCKET_INSTANCE__) {
      const oldInstance = window.__AYA_WEBSOCKET_INSTANCE__;
      oldInstance.disconnect();
      oldInstance.cleanup?.(); // Call cleanup if it exists
    }

    // Register this instance as the global singleton
    window.__AYA_WEBSOCKET_INSTANCE__ = this;
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    if (this.connectionStatus.isConnecting()) {
      logger.info('🔄 Already connecting, skipping...');
      return;
    }

    this.connectionStatus.setState(ConnectionState.CONNECTING);
    this.clearConnectionTimer();

    // Build WS URL
    // In production always use current origin (same host:port). In dev, allow override via VITE vars.
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsUrl = `${protocol}//${window.location.host}`;
    if (
      import.meta.env?.DEV &&
      (import.meta.env.VITE_SERVER_HOST || import.meta.env.VITE_SERVER_PORT)
    ) {
      const host = (import.meta.env.VITE_SERVER_HOST as string) ?? 'localhost';
      const port = (import.meta.env.VITE_SERVER_PORT as string) ?? '5000';
      wsUrl = `${protocol}//${host}:${port}`;
    }

    logger.info('🔗 Connecting to WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupWebSocketHandlers();

      // Set connection timeout
      this.connectionTimer = setTimeout(() => {
        logger.warn('⏰ Connection timeout - forcing retry');
        this.handleConnectionFailure('Connection timeout');
      }, this.connectionTimeout);
    } catch (error) {
      logger.error('❌ Failed to create WebSocket:', error);
      this.handleConnectionFailure(`Failed to create WebSocket: ${error}`);
    }
  }

  /**
   * Send message (with automatic queuing if disconnected)
   */
  send(webSocketMessage: WebSocketMessage): void {
    const channel = webSocketMessage.channelId || 'unknown';

    if (this.isConnected()) {
      try {
        const ws = this.ws;
        if (ws) {
          ws.send(JSON.stringify(webSocketMessage));
        } else {
          throw new Error('WebSocket is null while considered connected');
        }
        logger.info(`📤 [${channel}] Message sent:`, webSocketMessage);
      } catch (error) {
        logger.error('❌ Failed to send message:', error);
        // Queue the message if send fails
        this.messageQueue.enqueue(webSocketMessage);
      }
    } else {
      // Queue message for later delivery
      const messageId = this.messageQueue.enqueue(webSocketMessage);
      logger.info(`📦 [${channel}] Message queued (disconnected): ${messageId}`);
    }
  }

  /**
   * Send request and wait for response (request-response pattern)
   */
  async request(
    channelId: string,
    payload: unknown,
    options: RequestOptions = {}
  ): Promise<unknown> {
    const messageId = this.generateMessageId();
    const timeout = options.timeout || 5000;

    const webSocketMessage: WebSocketMessage = {
      type: 'request',
      channelId,
      payload,
      messageId,
    };

    return new Promise((resolve, reject) => {
      // Set timeout
      const timeoutHandle = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new Error(`Request timeout after ${timeout}ms`));
      }, timeout);

      // Store pending request
      this.pendingRequests.set(messageId, {
        resolve,
        reject,
        timeout: timeoutHandle,
        channelId,
        timestamp: Date.now(),
      });

      // Send request
      if (this.isConnected()) {
        try {
          const ws = this.ws;
          if (ws) {
            ws.send(JSON.stringify(webSocketMessage));
          } else {
            throw new Error('WebSocket is null while considered connected');
          }
          logger.info(`📤 [${channelId}] Request sent:`, { messageId, payload });
        } catch (error) {
          // Clean up on send failure
          clearTimeout(timeoutHandle);
          this.pendingRequests.delete(messageId);
          reject(new Error(`Failed to send request: ${error}`));
        }
      } else {
        // Queue the request if disconnected
        this.messageQueue.enqueue(webSocketMessage);
        logger.info(`📦 [${channelId}] Request queued (disconnected): ${messageId}`);
      }
    });
  }

  /**
   * Generate unique message ID
   */
  generateMessageId(): string {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2, 11);
    return `msg_${timestamp}_${randomSuffix}`;
  }

  /**
   * Handle incoming response messages
   */
  private handleResponse(webSocketMessage: WebSocketMessage): void {
    const { replyTo, payload, error } = webSocketMessage;

    if (!replyTo) {
      logger.warn('⚠️ Received response without replyTo field');
      return;
    }

    const pendingRequest = this.pendingRequests.get(replyTo);
    if (!pendingRequest) {
      logger.warn(`⚠️ Received response for unknown request: ${replyTo}`);
      return;
    }

    // Clean up
    clearTimeout(pendingRequest.timeout);
    this.pendingRequests.delete(replyTo);

    // Resolve or reject based on error field
    if (error) {
      logger.info(`❌ [${pendingRequest.channelId}] Request failed:`, {
        messageId: replyTo,
        error,
      });
      pendingRequest.reject(new Error(error));
    } else {
      logger.info(`✅ [${pendingRequest.channelId}] Request completed:`, {
        messageId: replyTo,
        payload,
      });
      pendingRequest.resolve(payload);
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Register message handler
   */
  onMessage(handler: (webSocketMessage: WebSocketMessage) => void): () => void {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register connection handler
   */
  onConnect(handler: () => void): () => void {
    this.connectHandlers.push(handler);
    // If already connected, trigger immediately
    if (this.isConnected()) {
      logger.info('🔗 Already connected, triggering handler immediately');
      handler();
    }
    return () => {
      this.connectHandlers = this.connectHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register disconnection handler
   */
  onDisconnect(handler: () => void): () => void {
    this.disconnectHandlers.push(handler);
    return () => {
      this.disconnectHandlers = this.disconnectHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register error handler
   */
  onError(handler: (error: Error) => void): () => void {
    this.errorHandlers.push(handler);
    return () => {
      this.errorHandlers = this.errorHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Get connection status info
   */
  getConnectionStatus() {
    const status = this.connectionStatus.getStatus();
    return {
      ...status,
      queuedMessages: this.messageQueue.getQueueSize(),
    };
  }

  /**
   * Сбрасывает intentionalDisconnect и reconnect counter, форсирует подключение.
   * Используется после re-login и при возврате на вкладку.
   */
  forceReconnect(): void {
    logger.info('🔄 Force reconnecting WebSocket...');
    this.isIntentionalDisconnect = false;
    this.connectionStatus.reset();
    this.connect();
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    logger.info('🔌 Disconnecting WebSocket...');
    this.isIntentionalDisconnect = true;
    this.clearReconnectTimer();
    this.clearConnectionTimer();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.connectionStatus.setState(ConnectionState.DISCONNECTED);
  }

  /**
   * Complete cleanup - dispose of all resources
   */
  cleanup(): void {
    logger.info('🧹 WebSocketConnection cleanup started...');

    // Disconnect and prevent reconnection
    this.isIntentionalDisconnect = true;

    // Clear all timers
    this.clearReconnectTimer();
    this.clearConnectionTimer();

    // Reject all pending requests
    this.pendingRequests.forEach((request, messageId) => {
      clearTimeout(request.timeout);
      request.reject(new Error(`Connection closed for messageId: ${messageId}`));
    });
    this.pendingRequests.clear();

    // Close WebSocket connection
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Clear all handlers to prevent memory leaks
    this.messageHandlers = [];
    this.connectHandlers = [];
    this.disconnectHandlers = [];
    this.errorHandlers = [];

    // Reset connection status
    this.connectionStatus.reset();

    logger.info('✅ WebSocketConnection cleanup complete');
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      logger.debug('✅ WebSocket connected');
      this.connectionStatus.setState(ConnectionState.CONNECTED);
      this.clearReconnectTimer();
      this.clearConnectionTimer();

      // Process any queued messages
      this.processMessageQueue();

      // Notify handlers
      this.connectHandlers.forEach(handler => handler());
    };

    this.ws.onmessage = (event: MessageEvent<string>) => {
      try {
        const raw: unknown = JSON.parse(event.data);
        const webSocketMessage = raw as WebSocketMessage;

        // Handle responses to pending requests
        if (webSocketMessage.type === 'response' && webSocketMessage.replyTo) {
          this.handleResponse(webSocketMessage);
          return;
        }

        // Handle regular messages and broadcasts
        this.messageHandlers.forEach(handler => handler(webSocketMessage));
      } catch (error: unknown) {
        logger.error('❌ Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onclose = event => {
      logger.info(`🔌 WebSocket disconnected ${event.code} ${event.reason} `);
      this.clearConnectionTimer();
      this.connectionStatus.setState(ConnectionState.DISCONNECTED);

      // Notify handlers
      this.disconnectHandlers.forEach(handler => handler());

      // Auto-reconnect unless it was intentional
      if (!this.isIntentionalDisconnect) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = error => {
      logger.error('❌ WebSocket error:', error);
      this.clearConnectionTimer();
      this.handleConnectionFailure('WebSocket connection error');

      // Notify handlers
      this.errorHandlers.forEach(handler => handler(new Error('WebSocket error')));
    };
  }

  /**
   * Handle connection failure
   */
  private handleConnectionFailure(reason: string): void {
    logger.error(`❌ Connection failed: ${reason}`);
    this.connectionStatus.setError(reason);
    this.connectionStatus.setState(ConnectionState.DISCONNECTED);

    if (!this.isIntentionalDisconnect) {
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) return; // Already scheduled

    const attempt = this.connectionStatus.incrementReconnectAttempts();

    if (attempt > this.maxReconnectAttempts) {
      logger.error(`❌ Max reconnection attempts (${this.maxReconnectAttempts}) reached`);
      return;
    }

    logger.info(`🔄 Scheduling reconnect attempt ${attempt} in ${this.reconnectInterval}ms...`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectInterval);
  }

  /**
   * Process queued messages from localStorage on startup
   */
  private async processQueueOnStartup(): Promise<void> {
    const queuedMessages = this.messageQueue.getQueuedMessages();

    if (queuedMessages.length > 0) {
      logger.info(`📦 Found ${queuedMessages.length} queued messages from previous session`);

      // Wait for connection before processing
      if (!this.isConnected()) {
        this.onConnect(() => {
          setTimeout(() => this.processMessageQueue(), 1000); // Small delay after connection
        });
      }
    }
  }

  /**
   * Process all queued messages
   */
  private async processMessageQueue(): Promise<void> {
    const queuedMessages = this.messageQueue.getQueuedMessages();

    if (queuedMessages.length === 0) return;

    logger.info(`📦 Processing ${queuedMessages.length} queued messages...`);

    for (const message of queuedMessages) {
      try {
        const ws = this.ws;
        if (this.isConnected() && ws) {
          ws.send(JSON.stringify(message.data));
          this.messageQueue.dequeue(message.id);
          logger.info(`✅ Queued message sent: ${message.id}`);
        } else {
          logger.warn('⚠️ Connection lost while processing queue');
          break;
        }
      } catch (error) {
        logger.error(`❌ Failed to send queued message ${message.id}:`, error);

        // Increment attempts or remove if max reached
        if (!this.messageQueue.incrementAttempts(message.id)) {
          logger.error(`❌ Message ${message.id} dropped after max attempts`);
        }
      }

      // Small delay between messages to avoid overwhelming
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    logger.info(
      `✅ Queue processing complete. ${this.messageQueue.getQueueSize()} messages remaining`
    );
  }

  /**
   * Clear reconnect timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Clear connection timer
   */
  private clearConnectionTimer(): void {
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }
  }
}
